import { useState, useEffect } from 'react';
import {
    IGetTransactionsStatData,
    IGetTransactionsStatDataFormatted,
    IGetTransactionsSummaryStatData,
    IGetTransactionsSummaryStatDataFormatted,
    IResponse,
} from '@api/types';
import api from 'src/store/api';
import {
    ITransactionOrderOption,
    ITransactionsFilterValues,
} from '@pages/statistics/transactions/interfaces';
import { getSharedParamsFromFilter } from './utils';

import { formatTransactionsResponse, formatSummaryResponse } from './formatters';

// Limit
const PER_PAGE = 20;
// Offset
const FIRST_PAGE = 1;

// todo: надо типизировать any после того, как типизируем общий хук useFilters
export const useTransactions = ({
    filters,
    sort,
}: {
    filters: ITransactionsFilterValues;
    sort: ITransactionOrderOption;
}): {
    data: {
        transactions: IGetTransactionsStatDataFormatted[];
        totalFound: number;
    };
    isFetching: boolean;
    fetchMore: () => void;
    isFetchingMore: boolean;
    refetch: (filtersParams: ITransactionsFilterValues) => void;
    canFetchMore: boolean;
    error: string;
} => {
    const [data, setData] = useState<{
        transactions: IGetTransactionsStatDataFormatted[];
        totalFound: number;
    }>({ transactions: [], totalFound: null });
    const [isFetching, setIsFetching] = useState<boolean>(false);
    const [isFetchingMore, setIsFetchingMore] = useState<boolean>(false);
    const [page, setPage] = useState<number>(FIRST_PAGE);
    const [canFetchMore, setCanFetchMore] = useState<boolean>(false);
    const [error, setError] = useState<string>('');

    const fetchTransactions = (
        type: 'refetch' | 'more',
        filtersParams?: ITransactionsFilterValues,
    ): void => {
        setError('');
        const { offerId, offerIds } = filtersParams || filters;
        if (offerIds || offerId) {
            const nextPage: number = type === 'refetch' ? FIRST_PAGE : page + 1;

            setPage(nextPage);

            if (type === 'refetch') {
                setIsFetching(!isFetching);
            } else {
                setIsFetchingMore(!isFetchingMore);
            }

            const sortValue = sort.orderDirection === 'asc' ? sort.order : `-${sort.order}`;

            const sharedParams = getSharedParamsFromFilter(filtersParams || filters);
            const params = {
                ...sharedParams,
                sort: sortValue,
                offerIds: offerId || offerIds,
                page: nextPage,
                perPage: PER_PAGE,
                fields:
                    'order_number,order_time,order_status,transaction_time,revenue,commission_user,creative_title,sub_title,user_click_id,offer_type,offer_id,currency,transactionId,product,product_link,type_id,advertForm,action_id',
            };

            api.transactions
                .getTransactions({ params })
                .then((res: IResponse<IGetTransactionsStatData[]>): void => {
                    const transactionsData = res.data;
                    setCanFetchMore(transactionsData && transactionsData.length >= PER_PAGE);

                    const resData =
                        type === 'refetch'
                            ? formatTransactionsResponse(transactionsData)
                            : [
                                  ...data.transactions,
                                  ...formatTransactionsResponse(transactionsData),
                              ];

                    setData({
                        transactions: resData,
                        totalFound: res.meta.totalFound as number,
                    });
                    setIsFetching(false);
                    setIsFetchingMore(false);
                })
                .catch(resError => {
                    const requestId = resError?.response?.headers?.get('request-id');
                    const errorMessage: string = requestId
                        ? `request-id: ${requestId}`
                        : String(resError);
                    setError(errorMessage);
                    setIsFetching(false);
                });
        }
    };

    useEffect((): void => {
        fetchTransactions('refetch');
        // eslint-disable-next-line
    }, [sort]);

    const fetchMore = (): void => {
        fetchTransactions('more');
    };

    const refetch = (filtersParams: ITransactionsFilterValues): void => {
        fetchTransactions('refetch', filtersParams);
    };

    return {
        data,
        isFetching,
        fetchMore,
        isFetchingMore,
        refetch,
        canFetchMore,
        error,
    };
};

// todo: надо типизировать any после того, как типизируем общий хук useFilters
export const useSummary = ({
    filters,
}: {
    filters: ITransactionsFilterValues;
}): {
    data: IGetTransactionsSummaryStatDataFormatted;
    isFetching: boolean;
    refetch: (params?: ITransactionsFilterValues) => void;
    error: string;
} => {
    const [data, setData] = useState<IGetTransactionsSummaryStatDataFormatted>(null);
    const [isFetching, setIsFetching] = useState<boolean>(false);
    const [error, setError] = useState<string>('');

    const fetchSummary = (filtersParams?: ITransactionsFilterValues): void => {
        setError('');
        const { offerId, offerIds } = filtersParams || filters;
        if (offerIds || offerId) {
            setIsFetching(true);
            setError(null);
            const params = {
                ...getSharedParamsFromFilter(filtersParams || filters),
                offerIds: offerId || offerIds,
            };
            api.transactions
                .getSummaryTransactions({ params })
                .then((res: IResponse<IGetTransactionsSummaryStatData>): void => {
                    const data2 = formatSummaryResponse(res.data);
                    setData(data2);
                    setIsFetching(false);
                })
                .catch(async resError => {
                    const requestId = resError.response?.headers?.get('request-id');
                    const errorMessage = requestId ? `request-id: ${requestId}` : String(resError);
                    setError(errorMessage);
                    setIsFetching(false);
                });
        }
    };

    useEffect(() => {
        fetchSummary();
        // eslint-disable-next-line
    }, []);

    const refetch = (filtersParams: ITransactionsFilterValues): void => {
        fetchSummary(filtersParams);
    };

    return { data, isFetching, refetch, error };
};
